import { Injectable } from '@angular/core';
import { HttpService } from '@app/core/http/http.service';

import { FilterView } from 'app/shared/models/ipm/FilterView';
import { ConfigReportsDto } from '@app/shared/models/ipm/dto/ConfigReportsDto';

import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import * as FileSaver from 'file-saver';
import { ReportStatus, ReportView, SendReportView } from '@app/shared/views/ReportView';
import { ReportCommissionedFilterView } from '@app/shared/views/ReportCommissionedFilterView';
import { CommonFilterComboView } from '@app/shared/models/ipm/view/CommonFilterComboView';
import { FilterIPMComboDto, FilterIPMResultDto } from '@app/shared/models/ipm/dto/FilterIPMResultDto';

@Injectable()
export class ReportService {

  private subjectGenerateReport = new Subject<ReportCommissionedFilterView>();
  observableGenerateReports$ = this.subjectGenerateReport.asObservable();

  private subjectReportsObtanied = new Subject<FilterView>();
  observableReportsObtanied$ = this.subjectReportsObtanied.asObservable();

  private sApiGetConfigReportsParameters = '/api/reports/configReports';
  private sApiGetAllReports = '/api/reports/commissioned/all';
  private sApiGetCommissionedReportFilter = '/api/reports/commissioned/filter';
  private sApiGetVolumeReports = '/api/reports/volume/all';
  private sApiGenerateReport = '/api/reports/generate';
  private sApiDownloadReport = '/api/reports/download';
  private sApiSendReport = '/api/reports/send';
  private sApiRemoveReport = '/api/reports/remove';

  constructor(private httpService: HttpService) { }

  public runGenerateReport(filterSelected: ReportCommissionedFilterView) {
    this.subjectGenerateReport.next(filterSelected);
	}

  public reportsObtanied() {
    this.subjectReportsObtanied.next(void 0);
  }

  public getDataFilter(filterSelected: ReportCommissionedFilterView): Observable<FilterIPMComboDto> {
    return this.httpService.post<FilterIPMResultDto>(this.sApiGetCommissionedReportFilter, filterSelected)
    .pipe(
      map( data => {
        return {
          airlinesAvailable: data.airlinesAvailable?.map(airline => new CommonFilterComboView(airline.airlineId, airline.airlineCode, airline.airlineName)),
          countriesAvailable: data.countriesAvailable?.map(country => new CommonFilterComboView(country.countryId, country.isoCode, country.countryName)),
          documentTypesAvailable: data.documentTypesAvailable?.map(documentType => new CommonFilterComboView(documentType.documentTypeId, documentType.type, documentType.description)),
          agentsAvailable: data.agentsAvailable?.map(agent => new CommonFilterComboView(agent.agencyId, agent.agentCode.toString(), agent.agentName)),
          agentGroupsAvailable: data.agentGroupsAvailable?.map(agentGroup => new CommonFilterComboView(agentGroup.agencyGroupId, agentGroup.agentGroupCode, agentGroup.agentGroupName)),
          contractsAvailable: data.contractsAvailable?.map(contract => new CommonFilterComboView(contract.contractId, contract.contractCode, contract.contractName)),
          rangeCalendarAvailable: data.rangeCalendarAvailable,
          currenciesAvailable: data.currenciesAvailable
        }
      })
    );
  }

  public getConfigReportsParameters(): Observable<ConfigReportsDto> {
    return this.httpService.get<ConfigReportsDto>(this.sApiGetConfigReportsParameters);
  }

  public getAllReports(): Observable<Array<ReportView>> {
    return this.httpService.get<Array<any>>(this.sApiGetAllReports)
    .pipe(
      map(
        reports => { return reports.map(report => this.buildReport(report)) }
      )
    );
  }

  public getAllVolumeReports(): Observable<Array<ReportView>> {
    return this.httpService.get<Array<ReportView>>(this.sApiGetVolumeReports);
  }

  public generateReport(filterSelected: ReportCommissionedFilterView): Observable<Array<ReportView>> {
    return this.httpService.post<Array<any>>(this.sApiGenerateReport, filterSelected)
    .pipe(
      map(
        reports => { return reports.map(report => this.buildReport(report)) }
      )
    );
  }

  public downloadReport(report: ReportView): Observable<any> {
    return this.httpService.get(this.sApiDownloadReport + '/' + report.reportId, { responseType: 'blob' })
    .pipe(
      map((blob) => {
        FileSaver.saveAs(blob, report.fileName + '.' + report.fileType);
      })
    );
  }

  public sendReport(sendReport: SendReportView): Observable<any> {
    return this.httpService.post<any>(this.sApiSendReport, sendReport);
  }

  public removeReport(reportId: string): Observable<Array<ReportView>> {
    return this.httpService.delete<Array<any>>(this.sApiRemoveReport + '/' + reportId)
    .pipe(
      map(
        reports => { return reports.map(report => this.buildReport(report)) }
      )
    );
  }

  private buildReport(report: any) : ReportView {
    let rp = new ReportView();
    rp.reportId = report.reportId;
    rp.airline = report.airline;
    rp.country = report.country;
    rp.periodFrom = new Date(report.startPeriod);
    rp.periodTo = new Date(report.endPeriod);
    if (report.endDate != null)
    {
      rp.endDate = new Date(report.endDate);
    }
    rp.currency = report.currency;
    rp.createDate = new Date(report.createDate);
    rp.status = ReportStatus[<string> report.status];
    rp.fileName = report.fileName;
    rp.fileType = report.fileType;
    rp.filtersJson = report.filtersJson;

    return rp;
  }
}
